// Pre-Approval milestones — shared row layout used by both the
// Applications and Letters sections. Renders the party label, status
// chip, three sequential milestone buttons, per-milestone timestamp +
// attester display name, the optional document-link affordance, and a
// notes textarea. The two sections differ only in (a) the milestone
// labels for stage #3 (Received vs Received-Signed) and (b) the
// permission gate.

import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { CheckCircle2, ExternalLink, FileText, Save, Unlink } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { Textarea } from "@/components/ui/Select";
import { ApiError } from "@/api/client";
import { useToast } from "@/components/ui/Toast";
import type { PreApprovalPartyKind } from "@/types/deals";
import { useDateFormatter } from "@/utils/datetime";

export interface MilestoneSpec {
  key: "generated" | "sent" | "received";
  label: string;
  isComplete: boolean;
  enabled: boolean;
  at: string | null;
  by: string | null;
  onClick: () => Promise<unknown> | void;
  pending: boolean;
}

interface Props {
  dealId: string;
  party: PreApprovalPartyKind;
  statusLabel: string;
  statusTone: "draft" | "review" | "finalized";
  milestones: MilestoneSpec[];
  notes: string | null;
  onSaveNotes: (notes: string | null) => Promise<void>;
  notesPending: boolean;
  linkedDocumentId: string | null;
  onUnlinkDocument: () => Promise<void>;
  unlinkPending: boolean;
  onLinkDocument?: () => void;
  canEdit: boolean;
}

export function PreApprovalAttestationRow({
  dealId,
  party,
  statusLabel,
  statusTone,
  milestones,
  notes,
  onSaveNotes,
  notesPending,
  linkedDocumentId,
  onUnlinkDocument,
  unlinkPending,
  onLinkDocument,
  canEdit,
}: Props) {
  const toast = useToast();
  const [draftNotes, setDraftNotes] = useState(notes ?? "");
  useEffect(() => {
    setDraftNotes(notes ?? "");
  }, [notes]);

  const dirty = (notes ?? "") !== draftNotes;

  async function handleMilestone(m: MilestoneSpec) {
    try {
      await m.onClick();
    } catch (err) {
      // 409 from a re-mark is a soft success per spec — the row is
      // already at-or-past the requested status.
      if (err instanceof ApiError && err.status === 409) {
        toast.info(err.errors.join("; ") || "Already attested.");
        return;
      }
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not save the attestation.",
      );
    }
  }

  async function handleSaveNotes() {
    try {
      await onSaveNotes(draftNotes.trim() ? draftNotes : null);
      toast.success("Notes saved.");
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not save notes.",
      );
    }
  }

  async function handleUnlink() {
    try {
      await onUnlinkDocument();
      toast.success("Document unlinked.");
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not unlink document.",
      );
    }
  }

  return (
    <div className="border-t border-border-light pt-space-3 first:border-t-0 first:pt-0">
      <div className="flex items-center justify-between flex-wrap gap-space-2 mb-space-3">
        <div className="flex items-center gap-space-2">
          <span className="font-display font-bold text-onyx text-small">{party}</span>
          <Badge status={statusTone}>{statusLabel}</Badge>
        </div>
        <div className="text-caption text-gray-mid">Deal ID {dealId.slice(0, 8)}</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-2 mb-space-3">
        {milestones.map((m) => (
          <MilestoneCell
            key={m.key}
            label={m.label}
            isComplete={m.isComplete}
            enabled={m.enabled && canEdit && !m.pending}
            pending={m.pending}
            at={m.at}
            by={m.by}
            onClick={() => void handleMilestone(m)}
            disabledReason={
              !canEdit
                ? "You don't have permission to attest."
                : m.isComplete
                  ? `Already ${m.label.replace(/^Mark /, "").toLowerCase()}.`
                  : !m.enabled
                    ? "Complete the prior milestone first."
                    : undefined
            }
          />
        ))}
      </div>

      <div className="mb-space-3">
        <label className="block font-display text-caption uppercase tracking-label text-gray-mid mb-space-1">
          Notes
        </label>
        <Textarea
          rows={2}
          value={draftNotes}
          onChange={(e) => setDraftNotes(e.target.value)}
          disabled={!canEdit}
          placeholder="Optional"
        />
        <div className="flex justify-end mt-space-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void handleSaveNotes()}
            disabled={!canEdit || !dirty || notesPending}
            loading={notesPending}
          >
            <Save size={12} aria-hidden /> Save notes
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-space-2 text-small">
        {linkedDocumentId ? (
          <div className="flex items-center gap-space-2">
            <FileText size={14} className="text-gold-shadow" aria-hidden />
            <RouterLink
              to={`/pipeline/${dealId}?tab=documents&documentId=${linkedDocumentId}`}
              className="text-gold-shadow hover:underline inline-flex items-center gap-space-1"
            >
              View linked document <ExternalLink size={12} aria-hidden />
            </RouterLink>
            {canEdit && (
              <Button
                variant="text"
                size="sm"
                onClick={() => void handleUnlink()}
                disabled={unlinkPending}
                loading={unlinkPending}
              >
                <Unlink size={12} aria-hidden /> Unlink
              </Button>
            )}
          </div>
        ) : null}
        {canEdit && onLinkDocument && (
          <Button variant="text" size="sm" onClick={onLinkDocument}>
            Link a document from the repository…
          </Button>
        )}
      </div>
    </div>
  );
}

function MilestoneCell({
  label,
  isComplete,
  enabled,
  pending,
  at,
  by,
  onClick,
  disabledReason,
}: {
  label: string;
  isComplete: boolean;
  enabled: boolean;
  pending: boolean;
  at: string | null;
  by: string | null;
  onClick: () => void;
  disabledReason?: string;
}) {
  const { dateTime } = useDateFormatter();
  return (
    <div className="border border-border-light rounded-md p-space-2 flex flex-col gap-space-1">
      {isComplete ? (
        <div className="inline-flex items-center gap-space-1 text-status-success font-display font-bold text-caption uppercase tracking-label">
          <CheckCircle2 size={14} aria-hidden /> {label.replace(/^Mark /, "")}
        </div>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          onClick={onClick}
          disabled={!enabled}
          loading={pending}
          title={disabledReason}
        >
          {label}
        </Button>
      )}
      {at && (
        <div className="text-caption text-gray-mid">
          {dateTime(at)}
          {by ? <> · {by}</> : null}
        </div>
      )}
    </div>
  );
}
